export type UpcAFormatResult = {
  barcodeValue: string | null;
  displayValue: string;
  error: string | null;
  length: number | null;
  sourceLength: number;
  wasCheckDigitAdded: boolean;
};

export const UPC_A_LENGTH = 12;
export const UPC_A_BASE_LENGTH = 11;

export const getDigitsOnly = (value: string | number | null | undefined): string => (
  value == null ? '' : value.toString().replace(/\D/g, '')
);

export const calculateUpcACheckDigit = (value: string): string | null => {
  const digits = getDigitsOnly(value);
  if (digits.length !== UPC_A_BASE_LENGTH) {
    return null;
  }

  const numericDigits = digits.split('').map(Number);
  const oddSum = numericDigits[0]
    + numericDigits[2]
    + numericDigits[4]
    + numericDigits[6]
    + numericDigits[8]
    + numericDigits[10];
  const evenSum = numericDigits[1]
    + numericDigits[3]
    + numericDigits[5]
    + numericDigits[7]
    + numericDigits[9];

  return String((10 - ((oddSum * 3 + evenSum) % 10)) % 10);
};

export const isValidUpcA = (value: string | number | null | undefined): boolean => {
  const digits = getDigitsOnly(value);
  if (digits.length !== UPC_A_LENGTH) {
    return false;
  }

  return calculateUpcACheckDigit(digits.slice(0, UPC_A_BASE_LENGTH)) === digits[UPC_A_BASE_LENGTH];
};

export const formatPluAsUpcA = (plu: string | number | null | undefined): UpcAFormatResult => {
  const digits = getDigitsOnly(plu);
  const sourceLength = digits.length;

  if (!digits) {
    return {
      barcodeValue: null,
      displayValue: '-',
      error: 'PLU barcode not available',
      length: null,
      sourceLength,
      wasCheckDigitAdded: false,
    };
  }

  if (sourceLength === UPC_A_BASE_LENGTH) {
    const checkDigit = calculateUpcACheckDigit(digits);
    const barcodeValue = checkDigit ? `${digits}${checkDigit}` : null;

    return {
      barcodeValue,
      displayValue: barcodeValue || digits,
      error: barcodeValue ? null : 'PLU barcode not available',
      length: barcodeValue ? UPC_A_LENGTH : null,
      sourceLength,
      wasCheckDigitAdded: Boolean(barcodeValue),
    };
  }

  if (sourceLength === UPC_A_LENGTH) {
    const hasValidCheckDigit = isValidUpcA(digits);

    return {
      barcodeValue: hasValidCheckDigit ? digits : null,
      displayValue: digits,
      error: hasValidCheckDigit ? null : 'Invalid UPC-A check digit',
      length: UPC_A_LENGTH,
      sourceLength,
      wasCheckDigitAdded: false,
    };
  }

  return {
    barcodeValue: null,
    displayValue: digits,
    error: 'PLU must be 11 or 12 digits for UPC-A',
    length: sourceLength,
    sourceLength,
    wasCheckDigitAdded: false,
  };
};
